import { useEffect, useRef } from 'react';
import { Box, Divider, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { AiScoringLoader } from '../components';
import { AiScoreBreakdown } from '../AiScoreBreakdown';
import { AiScoreCandidateSelect } from './AiScoreCandidateSelect';
import { FileUpload } from '@/components/ats';
import { useAiScoreTest } from './hooks/useAiScoreTest';
import { Stars01 } from '@kula-ai/icons-core';
import { AiScoringConstants } from '../aiScoringConstants';
import { useShowToast } from '@/hooks';
import { useFileValidation } from '@/hooks/useFileValidation';
import { useKFlags } from '@/hooks/use-k-flags';

interface AiScoreTestingProps {
  jobId: string;
  autoStartWithSession?: boolean;
  shouldRerunAiTest?: boolean;
  onSessionChange?: (_session: any, _resultKey: string | null) => void;
  onTestStart?: () => void;
  onLoadingChange?: (_isLoading: boolean) => void;
}

export const AiScoreTesting: React.FC<AiScoreTestingProps> = ({
  jobId,
  autoStartWithSession = false,
  shouldRerunAiTest = false,
  onSessionChange,
  onTestStart,
  onLoadingChange,
}) => {
  const autoStartTriggeredRef = useRef(false);
  const toast = useShowToast();
  const { handleAttachmentValidation } = useFileValidation();
  const { feJobTeammates } = useKFlags();

  const {
    isTestMode,
    isLoading,
    session,
    scoreData,
    hasSession,
    resultKey,
    startTestWithResume,
    startTestWithContact,
    retestWithSession,
    testAgainNewCandidate,
    resetToTestMode,
    clearPolling,
  } = useAiScoreTest({ jobId });

  // Notify parent about session changes
  useEffect(() => {
    if (onSessionChange) {
      onSessionChange(session, resultKey);
    }
  }, [session, resultKey, onSessionChange]);

  // Notify parent when test starts (loading becomes true)
  useEffect(() => {
    if (isLoading && onTestStart) {
      onTestStart();
    }
  }, [isLoading, onTestStart]);

  // Notify parent about loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Reset the auto-start flag when shouldRerunAiTest changes to true
  useEffect(() => {
    if (shouldRerunAiTest) {
      autoStartTriggeredRef.current = false;
    }
  }, [shouldRerunAiTest]);

  // Auto-start logic
  useEffect(() => {
    if (
      autoStartWithSession &&
      !isLoading &&
      !autoStartTriggeredRef.current &&
      shouldRerunAiTest
    ) {
      if (hasSession) {
        // If we have an existing session, check if we need to reset to test mode first
        if (!isTestMode) {
          resetToTestMode();
          // After resetting, the next render will trigger the retest
          return;
        }

        // If we're in test mode and have a session, rerun with it
        if (isTestMode) {
          autoStartTriggeredRef.current = true;
          retestWithSession();
        }
      } else if (!isTestMode) {
        // If we need to rerun but don't have a session, reset to test mode
        autoStartTriggeredRef.current = true; // Set flag to prevent repeated resets
        resetToTestMode();
      }
    }
  }, [
    autoStartWithSession,
    hasSession,
    isTestMode,
    isLoading,
    shouldRerunAiTest,
    retestWithSession,
    resetToTestMode,
  ]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  // Show loading state
  if (isLoading) {
    return (
      <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
        <AiScoringLoader
          isStep
          config={
            feJobTeammates
              ? AiScoringConstants.loaderSteps.testingScreener
              : AiScoringConstants.loaderSteps.testing
          }
        />
      </Flex>
    );
  }

  const showScoreBreakdown = scoreData && !isTestMode;

  // Show score breakdown if we have score data
  if (showScoreBreakdown) {
    return (
      <Flex w="100%" h="100%">
        <AiScoreBreakdown
          data={scoreData?.data}
          session={session}
          retest={retestWithSession}
          testAgainNewCandidate={testAgainNewCandidate}
          application_id={session?.contact?.id}
          ats_job_id={parseInt(jobId)}
        />
      </Flex>
    );
  }

  // Show test mode UI
  const header = (
    <Flex gap="8px" alignItems="center" pb="4px">
      <Stars01
        size={20}
        gradient={{
          colors: [
            { offset: '0%', color: '#8A90FF' },
            { offset: '100%', color: '#B15FDA' },
          ],
        }}
      />{' '}
      <KText
        fontSize="16px"
        sx={{
          background: 'linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent',
          fontVariationSettings: "'opsz' 18",
        }}
      >
        {feJobTeammates ? 'Test the Scoring agent' : 'Test AI scoring'}
      </KText>
    </Flex>
  );

  const subtitle =
    "Upload your ideal candidate's resume or select an existing candidate";

  const handleFileUpload = async (files, unAcceptedFiles) => {
    if (unAcceptedFiles && unAcceptedFiles.length > 0) {
      unAcceptedFiles.forEach((rejection) => {
        const file = rejection.file;
        // Local validation - check file size
        if (file.size > 10 * 1024 * 1024) {
          toast({
            title: 'File is too large. Only files up to 10 MB are supported',
            status: 'error',
          });
        }
      });
      return;
    }

    // Process accepted files with signature validation
    if (files.length > 0) {
      const isValid = await handleAttachmentValidation({
        files,
        toastConfig: {
          title:
            'Invalid file type. Only PDF, DOC and DOCX files are supported',
        },
      });
      if (isValid) {
        startTestWithResume(files[0]);
      }
    }
  };

  return (
    <Flex
      w="100%"
      h="100%"
      flexDir="column"
      justifyContent="center"
      alignItems="center"
      mt="-5%"
      p={showScoreBreakdown ? '0' : '12px'}
    >
      {header}
      <KText textAlign="center" maxW="600px">
        {subtitle}
      </KText>

      <FileUpload
        maxFiles={1}
        acceptedFiles=".pdf,.doc,.docx"
        handleUpload={handleFileUpload}
        borderColor="primary-300"
        description="Supported : PDF, DOC, DOCX (Max 10 MB)"
        maxSize={10 * 1024 * 1024}
        wrapperProps={{
          width: '454px',
          height: '147px',
          mt: '32px',
        }}
      />
      <Flex gap="8px" alignItems="center" w="50%" m="24px 0px">
        <Divider />
        <KText variant="heading_4" p="0 8px">
          OR
        </KText>
        <Divider />
      </Flex>
      <Box w="467px">
        <AiScoreCandidateSelect
          onSelect={(candidate) => {
            if (candidate?.id) {
              startTestWithContact(candidate.id);
            }
          }}
        />
      </Box>
    </Flex>
  );
};
